import './post.css';
import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import isVideoFile from '../../utils/isVideoFile';
import {
  normalizeMediaList,
  buildNormalizedUrl,
  derivePoster,
} from '../../utils/postMediaUtils';

export default function PostMediaCarousel({
  item,
  autoPlay = true,
  enableZoom = true,
  maxHeight,
  onMediaIndexChange,
  initialIndex = null,
}) {
  const mediaList = useMemo(() => normalizeMediaList(item), [item]);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [thumbNav, setThumbNav] = useState({ canPrev: false, canNext: false });
  const [isVideo, setIsVideo] = useState(false);
  const [normalizedUrl, setNormalizedUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);
  const [mediaDims, setMediaDims] = useState({ w: 0, h: 0 });

  const isPortrait = mediaDims.h > mediaDims.w && mediaDims.w > 0;
  const videoRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const thumbsRef = useRef(null);
  const playRequestedRef = useRef(false);

  const currentMedia = mediaList[mediaIndex] || null;
  const activeUrl = currentMedia?.url || '';
  const mediaKey = useMemo(
    () =>
      mediaList
        .map((m) => m?.id || m?.publicId || m?.url || m?.secure_url || '')
        .join('|'),
    [mediaList],
  );
  const startIndex = useMemo(() => {
    if (Number.isInteger(initialIndex)) return initialIndex;
    if (Number.isInteger(item?.coverIndex)) return item.coverIndex;
    return 0;
  }, [initialIndex, item?.coverIndex]);

  useEffect(() => {
    if (!mediaList.length) {
      setMediaIndex(0);
      return;
    }
    const nextIndex = Math.min(startIndex, mediaList.length - 1);
    setMediaIndex((prev) =>
      prev === nextIndex && mediaKey ? prev : nextIndex,
    );
  }, [mediaKey, mediaList.length, startIndex]);

  useEffect(() => {
    if (typeof onMediaIndexChange !== 'function') return;
    onMediaIndexChange(mediaIndex, currentMedia);
    // Intentionally exclude onMediaIndexChange from deps to avoid re-run loops
    // when parent recreates the callback after every setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaIndex, currentMedia]);

  useEffect(() => {
    if (!activeUrl) return;
    const vid = isVideoFile(activeUrl);
    setIsVideo(vid);
    const norm = buildNormalizedUrl(activeUrl);
    setNormalizedUrl(norm);
    setPosterUrl(vid ? derivePoster(norm) : '');
    setMediaLoading(true);
    setMediaError(false);
    setIsImageZoomOpen(false);
  }, [activeUrl]);

  useEffect(() => {
    if (!isVideo || !autoPlay) return;
    const el = mediaContainerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setIsInView(entry.isIntersecting));
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isVideo, autoPlay]);

  useEffect(() => {
    if (!isVideo || !autoPlay || !videoRef.current) return;
    const v = videoRef.current;
    const shouldPlay = isInView && !document.hidden;
    if (shouldPlay) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
    const onVis = () => {
      if (!videoRef.current) return;
      if (isInView && !document.hidden) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isVideo, isInView, autoPlay]);

  useEffect(() => {
    if (!isImageZoomOpen || !enableZoom) return;
    const handleKey = (event) => {
      if (event.key === 'Escape') setIsImageZoomOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isImageZoomOpen, enableZoom]);

  const retryMedia = () => {
    setMediaError(false);
    setMediaLoading(true);
    if (videoRef.current) {
      videoRef.current.load();
      if (autoPlay) {
        videoRef.current.play().catch(() => {});
      }
    }
  };

  const onVideoEnter = useCallback(() => {
    if (!autoPlay || !videoRef.current) return;
    if (!playRequestedRef.current) {
      playRequestedRef.current = true;
      videoRef.current.muted = false;
    }
  }, [autoPlay]);

  const hasMultipleMedia = mediaList.length > 1;
  const thumbsPerPage = 5;
  const thumbSize = 72;
  const thumbGap = 8;
  const thumbArrowGutter = 32;
  const thumbViewportWidth =
    thumbSize * thumbsPerPage + thumbGap * (thumbsPerPage - 1);
  const shouldCarouselThumbs = mediaList.length > thumbsPerPage;

  const goPrevMedia = useCallback(() => {
    if (!hasMultipleMedia) return;
    setMediaIndex((idx) => (idx - 1 + mediaList.length) % mediaList.length);
  }, [hasMultipleMedia, mediaList.length]);

  const goNextMedia = useCallback(() => {
    if (!hasMultipleMedia) return;
    setMediaIndex((idx) => (idx + 1) % mediaList.length);
  }, [hasMultipleMedia, mediaList.length]);

  const updateThumbNav = useCallback(() => {
    const el = thumbsRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= maxScrollLeft - 1;
    setThumbNav((prev) => {
      const next = { canPrev: !atStart, canNext: !atEnd };
      if (prev.canPrev === next.canPrev && prev.canNext === next.canNext) {
        return prev;
      }
      return next;
    });
  }, []);

  const scrollThumbsBy = useCallback(
    (direction) => {
      const el = thumbsRef.current;
      if (!el) return;
      const delta = direction * (thumbSize + thumbGap) * thumbsPerPage;
      el.scrollBy({ left: delta, behavior: 'smooth' });
    },
    [thumbGap, thumbSize, thumbsPerPage],
  );

  useEffect(() => {
    if (!shouldCarouselThumbs) {
      if (thumbsRef.current) thumbsRef.current.scrollLeft = 0;
      setThumbNav((prev) => {
        if (!prev.canPrev && !prev.canNext) return prev;
        return { canPrev: false, canNext: false };
      });
      return;
    }
    updateThumbNav();
  }, [mediaList.length, shouldCarouselThumbs, updateThumbNav]);

  if (!mediaList.length) return null;

  const resolvedMaxHeight = maxHeight || (isPortrait ? '80vh' : '75vh');

  return (
    <>
      {isImageZoomOpen && enableZoom && !isVideo && normalizedUrl && (
        <div
          className="postImageZoom"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsImageZoomOpen(false)}
        >
          <div
            className="postImageZoom__inner"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="postImageZoom__close"
              onClick={() => setIsImageZoomOpen(false)}
              aria-label="Close image"
            >
              X
            </button>
            <img src={normalizedUrl} alt="" />
          </div>
        </div>
      )}
      <div
        ref={mediaContainerRef}
        className="postMediaWrapper"
        style={{
          position: 'relative',
          width: isPortrait ? 'min(100%, 420px)' : '100%',
          margin: isPortrait ? '0 auto' : undefined,
          aspectRatio:
            mediaDims.w && mediaDims.h
              ? `${mediaDims.w} / ${mediaDims.h}`
              : isVideo
              ? '16 / 9'
              : 'auto',
          height:
            !('aspectRatio' in document.documentElement.style) &&
            mediaDims.w &&
            mediaDims.h
              ? `${Math.round((mediaDims.h / mediaDims.w) * 100)}vw`
              : undefined,
          maxHeight: resolvedMaxHeight,
          overflow: 'hidden',
          borderRadius: 8,
          backgroundColor: '#000',
          backgroundImage: mediaLoading
            ? 'linear-gradient(110deg,#222 8%,#333 18%,#222 33%)'
            : 'none',
          backgroundSize: mediaLoading ? '200% 100%' : undefined,
          animation: mediaLoading ? 'shimmer 1.2s linear infinite' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        tabIndex={hasMultipleMedia ? 0 : -1}
        onKeyDown={(event) => {
          if (!hasMultipleMedia) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goPrevMedia();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            goNextMedia();
          }
        }}
      >
        {isVideo ? (
          <>
            <video
              ref={videoRef}
              src={normalizedUrl}
              poster={posterUrl || undefined}
              muted
              loop
              playsInline
              preload="metadata"
              controls
              onLoadedMetadata={() => {
                try {
                  if (videoRef.current) {
                    const vw = videoRef.current.videoWidth;
                    const vh = videoRef.current.videoHeight;
                    if (vw && vh) setMediaDims({ w: vw, h: vh });
                  }
                } catch {}
              }}
              onLoadedData={() => setMediaLoading(false)}
              onError={() => {
                setMediaError(true);
                setMediaLoading(false);
              }}
              onMouseEnter={autoPlay ? onVideoEnter : undefined}
              style={{
                width: '100%',
                height: '100%',
                objectFit: isPortrait ? 'contain' : 'cover',
                display: mediaError ? 'none' : 'block',
                backgroundColor: '#000',
              }}
            />
            {mediaError && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontSize: 12,
                  gap: 8,
                  textAlign: 'center',
                  padding: 12,
                }}
              >
                <span>Video failed to load.</span>
                <button
                  onClick={retryMedia}
                  style={{
                    background: '#14b866',
                    color: '#fff',
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Retry
                </button>
                <a
                  href={normalizedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#9fdfff',
                    textDecoration: 'underline',
                  }}
                >
                  Open Direct
                </a>
              </div>
            )}
            {mediaLoading && !mediaError && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                  fontSize: 12,
                  letterSpacing: 1,
                }}
              >
                Loading
              </div>
            )}
          </>
        ) : activeUrl ? (
          <button
            type="button"
            className="postImageButton"
            onClick={() => {
              if (!enableZoom || mediaLoading || mediaError) return;
              setIsImageZoomOpen(true);
            }}
            aria-label="View image"
          >
            <img
              src={normalizedUrl}
              alt=""
              loading="lazy"
              onLoad={(event) => {
                try {
                  const el = event.currentTarget;
                  if (el.naturalWidth && el.naturalHeight) {
                    setMediaDims({
                      w: el.naturalWidth,
                      h: el.naturalHeight,
                    });
                  }
                } catch {}
                setMediaLoading(false);
              }}
              onError={() => {
                setMediaError(true);
                setMediaLoading(false);
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: mediaDims.h > mediaDims.w ? 'contain' : 'cover',
                display: mediaError ? 'none' : 'block',
                backgroundColor: '#000',
              }}
            />
          </button>
        ) : null}

        {mediaError && !isVideo && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              fontSize: 12,
              flexDirection: 'column',
              gap: 6,
              padding: 10,
            }}
          >
            <span>Media not available.</span>
            <a
              href={activeUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#9fdfff',
                textDecoration: 'underline',
              }}
            >
              Open Direct
            </a>
          </div>
        )}
      </div>
      {hasMultipleMedia && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 6,
            }}
          >
            {mediaList.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setMediaIndex(idx)}
                aria-label={`Go to media ${idx + 1}`}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  border: 'none',
                  background:
                    idx === mediaIndex
                      ? '#fff'
                      : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            ))}
          </div>
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              marginTop: 8,
              minWidth: 0,
            }}
          >
            {shouldCarouselThumbs && (
              <button
                type="button"
                aria-label="Scroll thumbnails left"
                onClick={() => scrollThumbsBy(-1)}
                disabled={!thumbNav.canPrev}
                style={{
                  position: 'absolute',
                  left: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  zIndex: 2,
                  opacity: thumbNav.canPrev ? 1 : 0.4,
                }}
              >
                {'<'}
              </button>
            )}
            <div
              ref={thumbsRef}
              onScroll={shouldCarouselThumbs ? updateThumbNav : undefined}
              className="postMediaThumbs"
              style={{
                display: 'flex',
                gap: thumbGap,
                overflowX: 'auto',
                paddingBottom: 4,
                width: '100%',
                maxWidth: shouldCarouselThumbs
                  ? `${thumbViewportWidth + thumbArrowGutter * 2}px`
                  : '100%',
                boxSizing: 'border-box',
                scrollSnapType: shouldCarouselThumbs ? 'x mandatory' : 'none',
                scrollBehavior: shouldCarouselThumbs ? 'smooth' : 'auto',
                paddingLeft: shouldCarouselThumbs ? thumbArrowGutter : 0,
                paddingRight: shouldCarouselThumbs ? thumbArrowGutter : 0,
                margin: shouldCarouselThumbs ? '0 auto' : undefined,
                minWidth: 0,
              }}
            >
              {mediaList.map((m, idx) => (
                <button
                  key={m.url || idx}
                  type="button"
                  onClick={() => setMediaIndex(idx)}
                  style={{
                    border:
                      idx === mediaIndex
                        ? '2px solid #14b866'
                        : '1px solid #444',
                    padding: 0,
                    borderRadius: 6,
                    background: '#111',
                    cursor: 'pointer',
                    width: thumbSize,
                    height: thumbSize,
                    flex: '0 0 auto',
                    overflow: 'hidden',
                    scrollSnapAlign: shouldCarouselThumbs ? 'start' : 'none',
                  }}
                  aria-label={`Select media ${idx + 1}`}
                >
                  {m.type === 'video' ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: '#000',
                        fontSize: 12,
                      }}
                    >
                      VIDEO
                    </div>
                  ) : (
                    <img
                      src={m.url}
                      alt="thumb"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      loading="lazy"
                    />
                  )}
                </button>
              ))}
            </div>
            {shouldCarouselThumbs && (
              <button
                type="button"
                aria-label="Scroll thumbnails right"
                onClick={() => scrollThumbsBy(1)}
                disabled={!thumbNav.canNext}
                style={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  zIndex: 2,
                  opacity: thumbNav.canNext ? 1 : 0.4,
                }}
              >
                {'>'}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
